import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";

const MONTHS_EL = [
  "Ιανουάριος", "Φεβρουάριος", "Μάρτιος", "Απρίλιος", "Μάιος", "Ιούνιος",
  "Ιούλιος", "Αύγουστος", "Σεπτέμβριος", "Οκτώβριος", "Νοέμβριος", "Δεκέμβριος",
];
const DAYS_EL = ["Δε", "Τρ", "Τε", "Πε", "Πα", "Σα", "Κυ"];

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const parseISODate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const getRateForDay = (rules, basePrice, isoDate) => {
  for (const r of rules || []) {
    if (!r.start || !r.end) continue;
    if (isoDate >= r.start && isoDate <= r.end) return Number(r.price);
  }
  return Number(basePrice || 0);
};

// Merge/dedupe rules: returns sorted, non-overlapping rules.
// New rules override older ones for overlapping dates.
const normalizeRules = (rules) => {
  // Build a map of date -> rule (later rules win)
  const dayMap = new Map();
  for (const r of rules) {
    if (!r.start || !r.end || r.price === "" || r.price == null) continue;
    const s = parseISODate(r.start);
    const e = parseISODate(r.end);
    if (e < s) continue;
    const d = new Date(s);
    while (d <= e) {
      dayMap.set(toISODate(d), { price: Number(r.price), label: r.label || "" });
      d.setDate(d.getDate() + 1);
    }
  }
  // Group consecutive same-price-same-label days
  const days = [...dayMap.keys()].sort();
  const grouped = [];
  let cur = null;
  for (const iso of days) {
    const info = dayMap.get(iso);
    if (
      cur &&
      cur.price === info.price &&
      cur.label === info.label &&
      (() => {
        const next = new Date(parseISODate(cur.end));
        next.setDate(next.getDate() + 1);
        return toISODate(next) === iso;
      })()
    ) {
      cur.end = iso;
    } else {
      if (cur) grouped.push(cur);
      cur = { start: iso, end: iso, price: info.price, label: info.label };
    }
  }
  if (cur) grouped.push(cur);
  return grouped;
};

function MonthGrid({ year, month, rules, basePrice, selection, onDayClick }) {
  const total = daysInMonth(year, month);
  const first = new Date(year, month, 1);
  // 0=Sunday, we want Monday-first → shift
  const startOffset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);

  return (
    <div className="bg-white rounded-2xl border border-[#E9E2D3] p-4">
      <div className="text-[#1A2B4C] font-serif text-lg mb-2 text-center">
        {MONTHS_EL[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-[#6B705C] uppercase tracking-widest mb-1">
        {DAYS_EL.map((d) => (
          <div key={d} className="text-center py-1">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = toISODate(new Date(year, month, d));
          const rate = getRateForDay(rules, basePrice, iso);
          const isCustom = rate !== Number(basePrice || 0);
          const isSelected =
            selection &&
            selection.start &&
            iso >= selection.start &&
            (!selection.end || iso <= selection.end);
          return (
            <button
              key={i}
              onClick={() => onDayClick(iso)}
              className={`relative flex flex-col items-center justify-center aspect-square rounded-lg text-xs border transition-all ${
                isSelected
                  ? "bg-[#1A2B4C] text-white border-[#1A2B4C]"
                  : isCustom
                    ? "bg-[#F5F2EB] border-[#E9E2D3] text-[#1A2B4C] hover:border-[#1A2B4C]"
                    : "bg-white border-[#E9E2D3] text-[#6B705C] hover:border-[#1A2B4C]"
              }`}
              title={`${iso} — €${rate}`}
            >
              <span className="text-[11px] font-medium">{d}</span>
              <span className={`text-[9px] mt-0.5 ${isSelected ? "text-white/90" : isCustom ? "text-[#1A2B4C]" : "text-[#6B705C]"}`}>
                €{rate}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function PricingTab() {
  const [apts, setApts] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [rules, setRules] = useState([]);
  const [basePrice, setBasePrice] = useState(0);
  const [saving, setSaving] = useState(false);

  const [viewYear, setViewYear] = useState(new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());

  // Range selection for bulk edit
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [rangePrice, setRangePrice] = useState("");
  const [rangeLabel, setRangeLabel] = useState("");

  const selected = useMemo(() => apts.find((a) => a.id === selectedId), [apts, selectedId]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/apartments");
      setApts(r.data);
      if (!selectedId && r.data[0]) setSelectedId(r.data[0].id);
    } catch (e) {
      console.error("Failed to load apartments:", e);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (selected) {
      setRules(selected.seasonal_pricing || []);
      setBasePrice(selected.price_per_night || 0);
    }
  }, [selected]);

  const save = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const cleaned = normalizeRules(rules);
      await api.put(`/admin/apartments/${selectedId}`, {
        price_per_night: Number(basePrice),
        seasonal_pricing: cleaned,
      });
      toast.success("Πρότυπο τιμών αποθηκεύτηκε");
      setRules(cleaned);
      load();
    } catch (e) {
      toast.error("Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  const onDayClick = (iso) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(iso);
      setRangeEnd("");
    } else if (rangeStart && !rangeEnd) {
      if (iso < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(iso);
      } else {
        setRangeEnd(iso);
      }
    }
  };

  const applyRange = () => {
    if (!rangeStart || !rangeEnd || rangePrice === "") {
      toast.error("Συμπληρώστε ημερομηνίες και τιμή");
      return;
    }
    const newRule = {
      start: rangeStart,
      end: rangeEnd,
      price: Number(rangePrice),
      label: rangeLabel || "",
    };
    setRules([...rules, newRule]);
    setRangeStart("");
    setRangeEnd("");
    setRangePrice("");
    setRangeLabel("");
    toast.success("Κανόνας προστέθηκε. Πατήστε «Αποθήκευση».");
  };

  const removeRule = (i) => {
    setRules(rules.filter((_, idx) => idx !== i));
  };

  const updateRule = (i, field, value) => {
    const arr = [...rules];
    arr[i] = { ...arr[i], [field]: field === "price" ? value : value };
    setRules(arr);
  };

  const clearAll = () => {
    if (!window.confirm("Διαγραφή όλων των εποχιακών κανόνων;")) return;
    setRules([]);
  };

  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewYear(viewYear - 1);
      setViewMonth(11);
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewYear(viewYear + 1);
      setViewMonth(0);
    } else setViewMonth(viewMonth + 1);
  };

  const months = [0, 1, 2].map((i) => {
    let y = viewYear;
    let m = viewMonth + i;
    while (m > 11) {
      m -= 12;
      y += 1;
    }
    return { year: y, month: m };
  });

  const selection = { start: rangeStart, end: rangeEnd };

  const inp = "w-full bg-[#F5F2EB] border border-[#E9E2D3] rounded-xl px-3 py-2 text-[#1A2B4C] focus:outline-none focus:ring-2 focus:ring-[#1A2B4C]/30 text-sm";

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="font-serif text-2xl text-[#1A2B4C]">Pricing Calendar</h2>
          <p className="text-sm text-[#6B705C]">
            Κάντε κλικ σε ημέρες για να επιλέξετε εύρος. Ορίστε τιμή και πατήστε «Εφαρμογή στο εύρος».
            Έπειτα πατήστε «Αποθήκευση» για να ισχύσει.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            data-testid="pricing-apt-select"
            className={inp + " min-w-[200px]"}
          >
            {apts.map((a) => (
              <option key={a.id} value={a.id}>{a.name_el || a.id}</option>
            ))}
          </select>
          <button
            onClick={save}
            disabled={saving}
            data-testid="pricing-save"
            className="inline-flex items-center gap-2 bg-[#1A2B4C] text-white px-5 py-2.5 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? "Αποθήκευση..." : "Αποθήκευση"}
          </button>
        </div>
      </div>

      {/* Base price */}
      <div className="bg-white rounded-2xl border border-[#E9E2D3] p-4 mb-4 flex items-center gap-3">
        <CalIcon className="w-4 h-4 text-[#6B705C]" />
        <label className="text-xs uppercase tracking-widest text-[#6B705C]">Βασική τιμή (όταν δεν εφαρμόζεται κανόνας)</label>
        <input
          type="number"
          step="0.01"
          value={basePrice}
          onChange={(e) => setBasePrice(e.target.value)}
          data-testid="pricing-base"
          className={inp + " max-w-[140px]"}
        />
        <span className="text-[#6B705C]">€/βραδιά</span>
      </div>

      {/* Bulk range editor */}
      <div className="bg-[#F5F2EB] rounded-2xl border border-[#E9E2D3] p-4 mb-4">
        <p className="text-xs uppercase tracking-widest text-[#6B705C] mb-3">Μαζική επεξεργασία εύρους</p>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Από</label>
            <input type="date" value={rangeStart} onChange={(e) => setRangeStart(e.target.value)} className={inp} data-testid="pricing-range-start" />
          </div>
          <div className="md:col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Έως</label>
            <input type="date" value={rangeEnd} onChange={(e) => setRangeEnd(e.target.value)} className={inp} data-testid="pricing-range-end" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">€/βραδιά</label>
            <input type="number" step="0.01" value={rangePrice} onChange={(e) => setRangePrice(e.target.value)} className={inp} data-testid="pricing-range-price" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Ετικέτα (προαιρ.)</label>
            <input value={rangeLabel} onChange={(e) => setRangeLabel(e.target.value)} placeholder="π.χ. Αύγουστος" className={inp} />
          </div>
          <button
            onClick={applyRange}
            data-testid="pricing-apply-range"
            className="md:col-span-2 inline-flex items-center justify-center gap-2 bg-[#1A2B4C] text-white px-4 py-2.5 rounded-full text-xs uppercase tracking-widest hover:-translate-y-0.5 transition-transform"
          >
            <Plus className="w-3.5 h-3.5" /> Εφαρμογή
          </button>
        </div>
      </div>

      {/* Calendar nav */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-2 rounded-full hover:bg-[#F5F2EB]">
          <ChevronLeft className="w-5 h-5 text-[#1A2B4C]" />
        </button>
        <div className="text-sm text-[#6B705C] uppercase tracking-widest">
          {MONTHS_EL[months[0].month]} {months[0].year} — {MONTHS_EL[months[2].month]} {months[2].year}
        </div>
        <button onClick={nextMonth} className="p-2 rounded-full hover:bg-[#F5F2EB]">
          <ChevronRight className="w-5 h-5 text-[#1A2B4C]" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {months.map((m) => (
          <MonthGrid
            key={`${m.year}-${m.month}`}
            year={m.year}
            month={m.month}
            rules={rules}
            basePrice={basePrice}
            selection={selection}
            onDayClick={onDayClick}
          />
        ))}
      </div>

      {/* Rules list */}
      <div className="bg-white rounded-2xl border border-[#E9E2D3] p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-widest text-[#6B705C]">
            Ενεργοί κανόνες ({rules.length})
          </p>
          {rules.length > 0 && (
            <button onClick={clearAll} className="text-xs text-red-500 hover:underline">Διαγραφή όλων</button>
          )}
        </div>
        {rules.length === 0 ? (
          <p className="text-sm text-[#6B705C]">Δεν υπάρχουν εποχιακοί κανόνες. Χρησιμοποιείται η βασική τιμή €{basePrice}.</p>
        ) : (
          <div className="space-y-2">
            {rules.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-end" data-testid={`pricing-rule-${i}`}>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Ετικέτα</label>
                  <input className={inp} value={r.label || ""} onChange={(e) => updateRule(i, "label", e.target.value)} />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Από</label>
                  <input type="date" className={inp} value={r.start || ""} onChange={(e) => updateRule(i, "start", e.target.value)} />
                </div>
                <div className="col-span-3">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">Έως</label>
                  <input type="date" className={inp} value={r.end || ""} onChange={(e) => updateRule(i, "end", e.target.value)} />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] uppercase tracking-widest text-[#6B705C]">€</label>
                  <input type="number" step="0.01" className={inp} value={r.price ?? ""} onChange={(e) => updateRule(i, "price", e.target.value)} />
                </div>
                <button onClick={() => removeRule(i)} className="col-span-1 text-red-500 hover:bg-red-50 rounded-lg py-2" title="Διαγραφή">
                  <Trash2 className="w-4 h-4 mx-auto" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
